import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button } from '@ecom/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement, type SyntheticEvent, useEffect, useRef, useState } from 'react';

import {
  useAssistantDecisionMutation,
  useAssistantStatusQuery,
  useSendAssistantMessageMutation,
} from '../api';
import { ASSISTANT_OPEN_EVENT } from '../open';
import type { AssistantReply, ChatEntry, ProposedOperation } from '../types';

import { EventView, PendingCard } from './AssistantCards';

import { useSession } from '@/lib/session';

let sequence = 0;
const nextId = (): string => {
  sequence += 1;
  return `entry-${String(sequence)}`;
};

/**
 * The merchant dashboard's chatbot: a launcher in the corner of every authenticated screen, and a panel.
 *
 * Everything it does goes through `/merchant/assistant` on OUR API — the panel holds only what it displays: the
 * conversation's history and its pending decisions live on the server, scoped to this member and this shop. A change
 * of shop starts a new conversation. When an operation succeeded (`dataChanged`), every cached query is refreshed, so
 * the screen behind the panel shows the new state.
 */
export function AssistantPanel(): ReactElement | null {
  const { t, locale } = useI18n();
  const { activeShopId } = useSession();
  const queryClient = useQueryClient();
  const status = useAssistantStatusQuery();
  const send = useSendAssistantMessageMutation();
  const decide = useAssistantDecisionMutation();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [entries, setEntries] = useState<readonly ChatEntry[]>([]);
  const [pending, setPending] = useState<readonly ProposedOperation[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // A conversation belongs to one shop (the server scopes it so): switching shop starts afresh.
  useEffect(() => {
    setEntries([]);
    setPending([]);
    setConversationId(null);
  }, [activeShopId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [entries, pending]);

  // Opened from the top bar's sparkle button (see ../open.ts).
  useEffect(() => {
    const onOpen = (): void => {
      setOpen(true);
    };
    window.addEventListener(ASSISTANT_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(ASSISTANT_OPEN_EVENT, onOpen);
    };
  }, []);

  if (activeShopId === null) return null;

  const busy = send.isPending || decide.isPending;
  const enabled = status.data?.enabled === true;

  function absorb(reply: AssistantReply): void {
    setConversationId(reply.conversationId);
    setEntries((current) => [
      ...current,
      ...reply.events.map((event): ChatEntry => ({ kind: 'event', id: nextId(), event })),
    ]);
    setPending(reply.pending);
    if (reply.dataChanged) void queryClient.invalidateQueries();
  }

  function fail(error: unknown): void {
    // The server still holds what it had: a 404 means the conversation is gone, start a new one.
    if (
      typeof error === 'object' &&
      error !== null &&
      (error as { status?: number }).status === 404
    ) {
      setConversationId(null);
      setPending([]);
    }
    setEntries((current) => [
      ...current,
      { kind: 'error', id: nextId(), text: t(resolveErrorMessageKey(error)) },
    ]);
  }

  function onSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const message = draft.trim();
    if (message.length === 0 || busy) return;
    setDraft('');
    setPending([]);
    setEntries((current) => [...current, { kind: 'user', id: nextId(), text: message }]);
    send.mutate({ conversationId, message, locale }, { onSuccess: absorb, onError: fail });
  }

  function onDecide(approve: boolean): void {
    if (conversationId === null) return;
    decide.mutate({ conversationId, approve, locale }, { onSuccess: absorb, onError: fail });
  }

  function restart(): void {
    setEntries([]);
    setPending([]);
    setConversationId(null);
  }

  return (
    <>
      {open ? (
        <section
          aria-label={t('merchant.assistant.title')}
          className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] end-4 z-40 flex h-[min(640px,calc(100dvh-2rem))] w-[min(420px,calc(100vw-2rem))] sm:bottom-6 sm:end-6 anim-rise flex-col overflow-hidden rounded-[18px] border border-line bg-surface shadow-popover"
        >
          <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <div>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('merchant.assistant.title')}
              </h2>
              <p className="text-xs text-inkFaint">{t('merchant.assistant.subtitle')}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="secondary" onClick={restart} disabled={busy}>
                {t('merchant.assistant.newConversation')}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                }}
                className="press flex h-9 w-9 items-center justify-center rounded-[10px] text-inkFaint hover:bg-surfaceHover hover:text-ink"
                aria-label={t('merchant.assistant.close')}
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          </header>

          <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3" aria-live="polite">
            {!enabled ? (
              <p className="text-sm text-inkSoft">
                {status.isPending
                  ? t('merchant.common.loadingAria')
                  : t('merchant.assistant.unavailable')}
              </p>
            ) : entries.length === 0 ? (
              <div className="text-sm text-inkSoft">
                <p>{t('merchant.assistant.welcome')}</p>
                <p className="mt-2 text-xs text-inkFaint">{t('merchant.assistant.privacy')}</p>
              </div>
            ) : null}

            {entries.map((entry) =>
              entry.kind === 'user' ? (
                <div
                  key={entry.id}
                  className="max-w-[85%] self-end whitespace-pre-wrap rounded-2xl rounded-se-sm bg-surface2 px-3 py-2 text-sm text-ink"
                >
                  {entry.text}
                </div>
              ) : entry.kind === 'error' ? (
                <p
                  key={entry.id}
                  role="alert"
                  className="self-start rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
                >
                  {entry.text}
                </p>
              ) : (
                <EventView key={entry.id} event={entry.event} />
              ),
            )}

            {pending.length > 0 ? (
              <PendingCard proposals={pending} busy={decide.isPending} onDecide={onDecide} />
            ) : null}
            {busy ? (
              <p className="self-start text-xs text-inkFaint">{t('merchant.assistant.thinking')}</p>
            ) : null}
            <div ref={endRef} />
          </div>

          <form onSubmit={onSubmit} className="flex items-end gap-2 border-t border-line p-3">
            <textarea
              value={draft}
              onChange={(event) => {
                setDraft(event.currentTarget.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) onSubmit(event);
              }}
              rows={2}
              maxLength={4000}
              disabled={!enabled}
              placeholder={t('merchant.assistant.placeholder')}
              aria-label={t('merchant.assistant.placeholder')}
              className="ecom-input min-h-[2.75rem] flex-1 resize-none"
            />
            <Button
              type="submit"
              loading={send.isPending}
              disabled={!enabled || draft.trim().length === 0 || decide.isPending}
            >
              {t('merchant.assistant.send')}
            </Button>
          </form>
        </section>
      ) : null}
    </>
  );
}
